import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { createPartnerListingCorrection } from "../../api/partnerPortalApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  fetchCasaMapaPartnerListingHealthDetail,
  updateCasaMapaPartnerListingVisibility,
} from "../../services/casamapaPartnerAgencyApi";
import { useLocale, useCurrency } from "../../intl";
import "../../styles/partnerPortalDashboard.css";
import "../../styles/casamapaListingHealthDashboard.css";
import "../../styles/partnerListingsDashboard.css";

const COPY = "realEstate.casaMapa.admin.partnerListings";
const HEALTH_COPY = "realEstate.casaMapa.admin.listings";

function formatDetailLoadError(err, t) {
  const message = String(err?.message || "");
  if (/route not found/i.test(message)) {
    return t(`${COPY}.apiUnavailable`);
  }
  return message || t(`${COPY}.detail.loadError`);
}

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };

const ISSUE_CORRECTION_TYPE = {
  stale: "stale_listing",
  aging: "stale_listing",
  missing_price: "wrong_price",
  missing_location: "wrong_address",
};

const ISSUE_AFFECTED_FIELDS = {
  stale: ["freshness"],
  aging: ["freshness"],
  no_photos: ["photos"],
  limited_photos: ["photos"],
  missing_price: ["price"],
  missing_location: ["location"],
  incomplete_profile: ["completeness"],
  partial_profile: ["completeness"],
  missing_description: ["description"],
  missing_area: ["area"],
  missing_beds_baths: ["bedsBaths"],
  unmapped_coordinates: ["coordinates"],
};

function listingLocation(listing) {
  const parts = [listing.neighborhood, listing.municipality].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

function IssueCard({ issue, t, onReport }) {
  return (
    <article className={`cm-partner-listing-detail__issue cm-partner-listing-detail__issue--${issue.severity}`}>
      <div className="cm-partner-listing-detail__issue-head">
        <span className={`cm-listing-health__severity cm-listing-health__severity--${issue.severity}`}>
          {t(`${HEALTH_COPY}.severity.${issue.severity}`)}
        </span>
        <h3>{t(`${HEALTH_COPY}.issues.${issue.id}`)}</h3>
      </div>
      <p className="cm-partner-listing-detail__issue-hint">{t(`${HEALTH_COPY}.issueHints.${issue.id}`)}</p>
      <p className="cm-partner-listing-detail__issue-action">{t(`${COPY}.detail.fixActions.${issue.id}`)}</p>
      <button type="button" className="partner-dashboard-btn partner-dashboard-btn--primary" onClick={() => onReport(issue.id)}>
        {t(`${COPY}.detail.reportThisIssue`)}
      </button>
    </article>
  );
}

function FactRow({ label, value, hasIssue }) {
  return (
    <div className={`cm-partner-listing-detail__fact${hasIssue ? " has-issue" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function PartnerCasaMapaListingDetailPage() {
  const { listingId } = useParams();
  const { path } = usePartnerPortalBase();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const correctionRef = useRef(null);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [correctionType, setCorrectionType] = useState("other");
  const [proposedValue, setProposedValue] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [visibilityBusy, setVisibilityBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaPartnerListingHealthDetail(listingId);
      setPayload(result);
    } catch (err) {
      setPayload(null);
      setError(formatDetailLoadError(err, t));
    } finally {
      setLoading(false);
    }
  }, [listingId, t]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const listing = payload?.listing;
  const issues = useMemo(() => {
    const rows = payload?.issues || [];
    return [...rows].sort(
      (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
    );
  }, [payload?.issues]);

  const affectedFields = useMemo(() => {
    const fields = new Set();
    for (const issue of issues) {
      for (const field of ISSUE_AFFECTED_FIELDS[issue.id] || []) {
        fields.add(field);
      }
    }
    return fields;
  }, [issues]);

  const handleReportIssue = (issueId) => {
    setCorrectionType(ISSUE_CORRECTION_TYPE[issueId] || "other");
    setNotes(t(`${HEALTH_COPY}.issues.${issueId}`));
    correctionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitCorrection = async (event) => {
    event.preventDefault();
    if (!payload?.clientViewId || !listing?.id) return;

    try {
      setSubmitting(true);
      setSubmitMessage("");
      await createPartnerListingCorrection({
        clientViewId: payload.clientViewId,
        canonicalListingId: listing.id,
        correctionType,
        proposedValue: proposedValue.trim(),
        notes: notes.trim(),
      });
      setSubmitMessage(t(`${COPY}.detail.correctionSubmitted`));
      setProposedValue("");
      setNotes("");
    } catch (err) {
      setSubmitMessage(err.message || t(`${COPY}.detail.correctionFailed`));
    } finally {
      setSubmitting(false);
    }
  };

  const setListingVisibility = async (publicVisible) => {
    if (!listing?.id) return;
    try {
      setVisibilityBusy(true);
      await updateCasaMapaPartnerListingVisibility(listing.id, {
        publicVisible,
        confidenceScore: listing.confidenceScore ?? listing.healthScore,
      });
      await loadDetail();
    } catch (err) {
      setSubmitMessage(err.message || t(`${COPY}.sync.visibilityError`));
    } finally {
      setVisibilityBusy(false);
    }
  };

  const backHref = path("listings");

  return (
    <div className="cm-partner-listing-detail">
      <header className="cm-partner-listings__hero partner-dashboard-hero">
        <div>
          <p className="cm-partner-listings__eyebrow">{t(`${COPY}.detail.eyebrow`)}</p>
          <h1>{listing?.title || t(`${COPY}.detail.title`)}</h1>
          {listing ? (
            <p className="partner-dashboard-hero__intro">
              {listingLocation(listing)}
              {listing.agencyName ? ` · ${listing.agencyName}` : ""}
            </p>
          ) : (
            <p className="partner-dashboard-hero__intro">{t(`${COPY}.detail.subtitle`)}</p>
          )}
        </div>
        <div className="partner-dashboard-hero__actions">
          <Link className="partner-dashboard-btn" to={backHref}>
            {t(`${COPY}.detail.backToList`)}
          </Link>
          {listingId ? (
            <Link
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              to={path(`listings/${listingId}/comps`)}
            >
              {t(`${COPY}.detail.openComps`)}
            </Link>
          ) : null}
          {listing ? (
            <button
              type="button"
              className={`partner-dashboard-btn${listing.publicVisible === false ? " partner-dashboard-btn--primary" : ""}`}
              disabled={visibilityBusy}
              onClick={() => setListingVisibility(listing.publicVisible === false)}
            >
              {listing.publicVisible === false ? t(`${COPY}.sync.publish`) : t(`${COPY}.sync.hide`)}
            </button>
          ) : null}
          {listing?.publicUrl && listing.publicVisible !== false ? (
            <a className="partner-dashboard-btn" href={listing.publicUrl} target="_blank" rel="noreferrer">
              {t(`${COPY}.viewPublic`)}
            </a>
          ) : null}
          <button type="button" className="partner-dashboard-btn" onClick={loadDetail} disabled={loading}>
            {t(`${COPY}.refresh`)}
          </button>
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${COPY}.detail.loading`)}</p> : null}

      {!loading && listing ? (
        <div className="cm-partner-listing-detail__layout">
          <div className="cm-partner-listing-detail__main">
            <section className="partner-panel cm-partner-listing-detail__summary">
              <div className="cm-partner-listing-detail__summary-media">
                {listing.photoUrl ? (
                  <img src={listing.photoUrl} alt="" className="cm-partner-listing-detail__photo" />
                ) : (
                  <span className="cm-partner-listing-detail__photo cm-partner-listing-detail__photo--empty" aria-hidden="true">
                    ⌂
                  </span>
                )}
              </div>
              <div className="cm-partner-listing-detail__summary-copy">
                <p className="cm-partner-listing-detail__price">
                  {listing.priceCop != null ? formatMoney(listing.priceCop) : t(`${COPY}.detail.missingPrice`)}
                </p>
                <p className="muted">
                  {listing.listingType
                    ? t(`realEstate.casaMapa.admin.listingType.${listing.listingType}`, {}, listing.listingType)
                    : "—"}
                  {listing.propertyType ? ` · ${listing.propertyType}` : ""}
                </p>
                <div
                  className={`cm-partner-listing-detail__score cm-listing-health__score--${
                    payload?.healthLabel || listing.healthLabel || "fair"
                  }`}
                >
                  <div>
                    <span className="cm-partner-listing-detail__score-label">{t(`${COPY}.detail.scoreTitle`)}</span>
                    <strong className="cm-partner-listing-detail__score-value">
                      {payload?.healthScore ?? listing.healthScore ?? "—"}
                    </strong>
                    <span className="cm-partner-listing-detail__score-band">
                      {t(
                        `${COPY}.healthLabels.${payload?.healthLabel || listing.healthLabel || "fair"}`
                      )}
                    </span>
                  </div>
                  <p className="muted">{t(`${COPY}.detail.scoreHint`)}</p>
                </div>
                <div className="cm-partner-listing-detail__facts">
                  <FactRow
                    label={t(`${COPY}.meta.confidence`)}
                    value={listing.confidenceScore != null || listing.healthScore != null
                      ? String(listing.confidenceScore ?? listing.healthScore)
                      : "—"}
                  />
                  <FactRow
                    label={t(`${COPY}.meta.completeness`)}
                    value={listing.completenessScore != null ? `${listing.completenessScore}%` : "—"}
                    hasIssue={affectedFields.has("completeness")}
                  />
                  <FactRow
                    label={t(`${COPY}.meta.freshness`)}
                    value={t(`${HEALTH_COPY}.freshness.${listing.freshnessStatus || "unknown"}`)}
                    hasIssue={affectedFields.has("freshness")}
                  />
                  <FactRow
                    label={t(`${COPY}.meta.photos`)}
                    value={listing.imageCount ?? 0}
                    hasIssue={affectedFields.has("photos")}
                  />
                  <FactRow
                    label={t(`${COPY}.detail.fields.area`)}
                    value={listing.areaSqm != null ? `${listing.areaSqm} m²` : "—"}
                    hasIssue={affectedFields.has("area")}
                  />
                  <FactRow
                    label={t(`${COPY}.detail.fields.bedsBaths`)}
                    value={`${listing.bedrooms ?? "—"} / ${listing.bathrooms ?? "—"}`}
                    hasIssue={affectedFields.has("bedsBaths")}
                  />
                  <FactRow
                    label={t(`${COPY}.detail.fields.location`)}
                    value={listingLocation(listing)}
                    hasIssue={affectedFields.has("location")}
                  />
                </div>
              </div>
            </section>

            <section className="partner-panel cm-partner-listing-detail__pricing-panel">
              <div className="cm-listing-health__panel-head">
                <h2>{t(`${COPY}.pricing.title`)}</h2>
                <p className="muted">{t(`${COPY}.pricing.hint`)}</p>
              </div>
              {payload?.pricingSignals?.available ? (
                <div className="cm-partner-listing-detail__pricing">
                  <p
                    className={`cm-partner-listing-detail__pricing-band cm-partner-listing-detail__pricing-band--${payload.pricingSignals.band}`}
                  >
                    {t(`${COPY}.pricing.bands.${payload.pricingSignals.band}`)}
                  </p>
                  {payload.pricingSignals.relativePositionPercent != null ? (
                    <p>
                      {t(`${COPY}.pricing.vsMedian`, {
                        percent:
                          payload.pricingSignals.relativePositionPercent > 0
                            ? `+${payload.pricingSignals.relativePositionPercent}`
                            : String(payload.pricingSignals.relativePositionPercent),
                      })}
                    </p>
                  ) : null}
                  {payload.pricingSignals.sampleSize != null ? (
                    <p className="muted">
                      {t(`${COPY}.pricing.sample`, {
                        count: payload.pricingSignals.sampleSize,
                        level: payload.pricingSignals.geographyLevel || "—",
                      })}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="muted">{t(`${COPY}.pricing.unavailable`)}</p>
              )}
            </section>

            <section className="partner-panel cm-partner-listing-detail__issues-panel">
              <div className="cm-listing-health__panel-head">
                <h2>{t(`${COPY}.detail.issuesTitle`)}</h2>
                <p className="muted">{t(`${COPY}.detail.issuesHint`)}</p>
              </div>

              {issues.length ? (
                <div className="cm-partner-listing-detail__issues">
                  {issues.map((issue) => (
                    <IssueCard key={issue.id} issue={issue} t={t} onReport={handleReportIssue} />
                  ))}
                </div>
              ) : (
                <div className="cm-partner-listing-detail__healthy">
                  <h3>{t(`${COPY}.detail.healthyTitle`)}</h3>
                  <p className="muted">{t(`${COPY}.detail.healthyBody`)}</p>
                </div>
              )}
            </section>

            {listing.description ? (
              <section className="partner-panel cm-partner-listing-detail__description">
                <h2>{t(`${COPY}.detail.fields.description`)}</h2>
                <p className={affectedFields.has("description") ? "has-issue-text" : undefined}>{listing.description}</p>
              </section>
            ) : affectedFields.has("description") || issues.some((issue) => issue.id === "missing_description") ? (
              <section className="partner-panel cm-partner-listing-detail__description has-issue-block">
                <h2>{t(`${COPY}.detail.fields.description`)}</h2>
                <p className="muted">{t(`${COPY}.detail.missingDescription`)}</p>
              </section>
            ) : null}
          </div>

          <aside className="partner-panel cm-partner-listing-detail__correction" ref={correctionRef}>
            <h2>{t(`${COPY}.detail.correctionTitle`)}</h2>
            <p className="muted">{t(`${COPY}.detail.correctionHint`)}</p>

            <form className="cm-partner-listing-detail__correction-form" onSubmit={submitCorrection}>
              <label className="partner-lead-note-field">
                <span>{t(`${COPY}.detail.correctionType`)}</span>
                <select value={correctionType} onChange={(event) => setCorrectionType(event.target.value)}>
                  <option value="wrong_price">{t(`${COPY}.detail.correctionTypes.wrong_price`)}</option>
                  <option value="stale_listing">{t(`${COPY}.detail.correctionTypes.stale_listing`)}</option>
                  <option value="wrong_address">{t(`${COPY}.detail.correctionTypes.wrong_address`)}</option>
                  <option value="other">{t(`${COPY}.detail.correctionTypes.other`)}</option>
                </select>
              </label>

              <label className="partner-lead-note-field">
                <span>{t(`${COPY}.detail.proposedValue`)}</span>
                <input
                  type="text"
                  value={proposedValue}
                  onChange={(event) => setProposedValue(event.target.value)}
                  placeholder={t(`${COPY}.detail.proposedValuePlaceholder`)}
                />
              </label>

              <label className="partner-lead-note-field">
                <span>{t(`${COPY}.detail.correctionNotes`)}</span>
                <textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} />
              </label>

              <button
                type="submit"
                className="partner-dashboard-btn partner-dashboard-btn--primary"
                disabled={submitting || !notes.trim()}
              >
                {submitting ? t(`${COPY}.detail.submitting`) : t(`${COPY}.detail.submitCorrection`)}
              </button>
            </form>

            {submitMessage ? <p className="cm-partner-listing-detail__submit-message">{submitMessage}</p> : null}

            <p className="cm-partner-listing-detail__listing-id muted">
              {t(`${COPY}.detail.listingId`, { id: listing.id })}
            </p>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
